// Chart rows for the POV tab's dashboards, from the `sf/team/pov-activity-summary/` response (the same figures
// as the POV Team Members table and the individual page's list, so none of them can disagree).

import type {
  PovActivityCounts,
  PovActivityNode,
  PovActivityOpportunity,
} from '../hooks/useSfTeamPovActivitySummary';
import type { ChartPoint } from './campaignLeaderCharts';
import { shortName } from './campaignLeaderCharts';

export type PovMetric = keyof PovActivityCounts;

/** One slice/bar per direct report for a metric (their `rollup`, so a sub-manager is their whole team); zeros are left out. */
export function povMetricByReportRows(tree: PovActivityNode | null | undefined, metric: PovMetric): ChartPoint[] {
  const rows: ChartPoint[] = [];
  for (const r of Object.values(tree?.reports ?? {})) {
    const value = Number(r.rollup?.[metric] ?? 0);
    if (value > 0) rows.push({ label: shortName(String(r.email || '')), value });
  }
  return rows.sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

/** Opportunities with at least one POV activity vs without; empty when there are no opportunities. */
export function povCoverageRows(counts: PovActivityCounts | null | undefined): ChartPoint[] {
  if (!counts || counts.opportunity_count <= 0) return [];
  const withPov = Math.min(counts.opps_with_pov_activity_count, counts.opportunity_count);
  return [
    { label: 'With POV activity', value: withPov },
    { label: 'No POV activity', value: counts.opportunity_count - withPov },
  ].filter((r) => r.value > 0);
}

/** Whole-number share of opportunities with a POV activity, or null with no opportunities. */
export function povCoveragePct(counts: PovActivityCounts | null | undefined): number | null {
  if (!counts || counts.opportunity_count <= 0) return null;
  return Math.round((counts.opps_with_pov_activity_count / counts.opportunity_count) * 100);
}

/** Opportunities per stage, largest first. */
export function povOppsByStageRows(opps: PovActivityOpportunity[]): ChartPoint[] {
  const byStage = new Map<string, number>();
  for (const o of opps) {
    const stage = o.stage || 'Unknown';
    byStage.set(stage, (byStage.get(stage) ?? 0) + 1);
  }
  return Array.from(byStage, ([label, value]) => ({ label, value })).sort(
    (a, b) => b.value - a.value || a.label.localeCompare(b.label),
  );
}

export const NO_PRODUCT_LABEL = 'Product not specified';

/**
 * The opportunities the POV Opportunities table lists, from the `opportunities-pov-lean` response: only those
 * with at least one POV product row.
 */
export function povOpportunitiesFromLean(data: any): any[] {
  if (!data || typeof data !== 'object') return [];
  const block = (data as any)[Object.keys(data)[0]];
  const opps: any[] = Array.isArray(block?.opportunities) ? block.opportunities : [];
  return opps.filter((o) => Array.isArray(o?.pov_rows) && o.pov_rows.length > 0 && Number(o?.pov_product_count ?? 0) > 0);
}

/** POVs per product across the opportunities' POV product rows, most first (a product's name is trimmed). */
export function povByProductRows(opps: any[]): ChartPoint[] {
  const byProduct = new Map<string, number>();
  for (const o of opps || []) {
    for (const row of o?.pov_rows ?? []) {
      const name = String(row?.product?.name ?? '').trim() || NO_PRODUCT_LABEL;
      byProduct.set(name, (byProduct.get(name) ?? 0) + 1);
    }
  }
  return Array.from(byProduct, ([label, value]) => ({ label, value })).sort(
    (a, b) => b.value - a.value || a.label.localeCompare(b.label),
  );
}
